import { HttpClient } from '@angular/common/http'
import { Injectable, inject } from '@angular/core'
import { filter, forkJoin, map, Observable, of, switchMap, take } from 'rxjs'

import {
  CollectionRecord,
  Enums,
  HuntPackageRecord,
  ThreatProfile,
} from '@cyborg-monorepo/hunter/common'

import { VerityEnvironmentService } from '../../../../environments/verity-environment.service'
import { HuntPackageService } from '../../shared/services/hunt-package.service'
import { ThreatProfileService } from '../../shared/services/threat-profile.service'
import { UserService } from '../../shared/services/user.service'

export interface CollectionData {
  collection: CollectionRecord
  huntPackages: Array<HuntPackageRecord>
  threatProfiles: Array<ThreatProfile>
}

@Injectable({
  providedIn: 'root',
})
export class CollectionService {
  private httpClient = inject(HttpClient)
  private huntPackageService = inject(HuntPackageService)
  private threatProfileService = inject(ThreatProfileService)
  private userService = inject(UserService)
  private verityEnvironmentService = inject(VerityEnvironmentService)

  public getCollectionByUUID = (uuid: string): Observable<CollectionRecord> => {
    return this.httpClient.get<CollectionRecord>(
      `${this.verityEnvironmentService.apiUrl}/es/cyborg-collection/${uuid}`,
    )
  }

  public getRelatedCollectionsByHuntPackageUUID = (
    uuid: string,
  ): Observable<Array<CollectionRecord>> => {
    return this.httpClient.get<Array<CollectionRecord>>(
      `${this.verityEnvironmentService.apiUrl}/es/cyborg-collection/related/${uuid}`,
    )
  }

  public getCollectionsByUUID(
    uuids: Array<string>,
  ): Observable<Array<CollectionRecord>> {
    const searchParams: { [param: string]: string | string[] } = { uuids }
    return this.httpClient.get<Array<CollectionRecord>>(
      `${this.verityEnvironmentService.apiUrl}/es/cyborg-collection/list/`,
      {
        params: searchParams,
      },
    )
  }

  public getEmergingThreatCollections(): Observable<Array<CollectionRecord>> {
    return this.httpClient.get<Array<CollectionRecord>>(
      `${this.verityEnvironmentService.apiUrl}/es/cyborg-collection/emergingThreats`,
    )
  }

  public resolveCollectionLinks = (
    collection: CollectionRecord,
  ): Observable<CollectionData> => {
    return this.userService.profile$.pipe(
      filter((profile) => profile !== null),
      take(1),
      switchMap((profile) => {
        const isTrial = profile?.company.status === Enums.CompanyStatus.TRIAL
        const allowedUUIDs = new Set(
          (profile?.company.trialAccessibleHuntPackages || []).map((u) =>
            u.toLowerCase(),
          ),
        )

        return forkJoin({
          huntPackages: this.huntPackageService
            .getHuntPackagesByUUIDs(collection.hunter_packages)
            .pipe(
              map((huntPackages) => {
                return huntPackages.sort((a, b) => {
                  if (isTrial) {
                    const aOk =
                      a.community || allowedUUIDs.has(a.UUID.toLowerCase())
                    const bOk =
                      b.community || allowedUUIDs.has(b.UUID.toLowerCase())
                    if (aOk !== bOk) return aOk ? -1 : 1
                  }
                  return (
                    new Date(b.last_updated).valueOf() -
                    new Date(a.last_updated).valueOf()
                  )
                })
              }),
            ),
          collection: of(collection),
          threatProfiles: this.threatProfileService
            .getThreatProfilesbyUUIDs(collection.threat_profiles)
            .pipe(
              map((threatProfiles) =>
                threatProfiles.sort((a, b) => a.title.localeCompare(b.title)),
              ),
            ),
        })
      }),
    )
  }
}
